import { isNullish } from '@y42/util/nullish';
import * as go from 'gojs';
import { IS_SIDEBAR_TYPE_PROP } from './../../common/config/common-prop-names';
import { groupMargin, updateGroupBoundaries } from './group-bounds-utils';

interface delta {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const groupOverrideResizingTool = (diagram: go.Diagram, category: string) => {
  const tool = diagram.toolManager.resizingTool;
  overrideResize(category, tool);
};

const overrideResize = (groupCategory: string, tool: go.ResizingTool) => {
  const resizeBase = tool.resize.bind(tool);

  tool.resize = (newLocalBounds: go.Rect) => {
    if (!tool.adornedObject) {
      return;
    }

    const node = tool.adornedObject.part;

    if (!node) {
      return;
    }

    if (node?.category === groupCategory) {
      resizeGroupMembers(tool, node as go.Group, newLocalBounds);
    }

    if (node.containingGroup?.category === groupCategory && !!node.containingGroup?.isSelected) {
      return;
    }

    resizeBase.call(tool, newLocalBounds);

    if (node.containingGroup?.category === groupCategory) {
      updateGroupBoundaries(node.containingGroup);
    }
  };
};

const resizeGroupMembers = (tool: go.ResizingTool, group: go.Group, newLocalBounds: go.Rect) => {
  if (!tool.adornedObject) {
    return;
  }

  const groupBounds = group.resizeObject.naturalBounds;
  const delta = {
    x: newLocalBounds.x - groupBounds.x,
    y: newLocalBounds.y - groupBounds.y,
    width: newLocalBounds.width - groupBounds.width,
    height: newLocalBounds.height - groupBounds.height,
  };

  const minSize = 10;

  const it = group.memberParts
    .filter((p) => p instanceof go.Node)
    .filter((p) => !!p.data[IS_SIDEBAR_TYPE_PROP]).iterator;

  while (it.next()) {
    const part = it.value;
    if (part.resizable) {
      const newSize = calculateNewSize(part, delta, minSize, groupBounds);
      if (isNullish(newSize)) {
        tool.diagram.invalidateDocumentBounds();
        tool.transactionResult = tool.name;
        tool.diagram.raiseDiagramEvent('PartResized', tool.adornedObject, tool.originalDesiredSize);
        tool.stopTool();
        break;
      }

      part.resizeObject.desiredSize = newSize;
    }
    const newLocation = calculateNewLocation(part, delta, group.actualBounds);
    part.position = newLocation;
  }
};

const calculateNewLocation = (part: go.Part, delta: delta, groupBounds: go.Rect) => {
  const x = part.actualBounds.x + part.resizeObject.actualBounds.x;
  const y = part.actualBounds.y + part.resizeObject.actualBounds.y;
  const groupX = groupBounds.x + groupMargin;
  const groupY = groupBounds.y + groupMargin;
  const groupWidth = groupBounds.width - 2 * groupMargin;
  const groupHeight = groupBounds.height - 2 * groupMargin;

  const relativeX = x - groupX;
  const relativeY = y - groupY;

  const xRatio = relativeX / groupWidth;
  const yRatio = relativeY / groupHeight;
  const newX = (groupWidth + delta.width) * xRatio + groupX - part.resizeObject.actualBounds.x;
  const newY = (groupHeight + delta.height) * yRatio + groupY - part.resizeObject.actualBounds.y;

  return new go.Point(newX, newY);
};

const calculateNewSize = (part: go.Part, delta: delta, minSize: number, groupBounds: go.Rect): go.Size | null => {
  const groupWidth = groupBounds.width - 2 * groupMargin;
  const groupHeight = groupBounds.height - 2 * groupMargin;

  const resizeObject = part.resizeObject;
  const { width, height } = resizeObject.naturalBounds;
  const wRatio = width / groupWidth;
  const hRatio = height / groupHeight;
  const newWidth = width + delta.width * wRatio;
  const newHeight = height + delta.height * hRatio;

  return newWidth <= minSize || newHeight <= minSize ? null : new go.Size(newWidth, newHeight);
};
